/**
 * Guest Trial Service
 * Manages client-side quota and limits for anonymous/guest users trying the app
 * without registration or login.
 */

const GUEST_TRIAL_STORAGE_KEY = 'cna_guest_trial_state_v1';
const GUEST_SESSION_ID_KEY = 'cna_guest_session_id';
const DEFAULT_GUEST_LIMIT = 50;

export interface GuestTrialState {
  sessionId?: string;
  questionsAnswered: number;
  flashcardsViewed: number;
  totalItemsExplored: number;
  examsCompleted: number;
  answeredQuestionIds: string[];
  viewedFlashcardIds: string[];
  completedExamIds: string[];
  firstActiveTimestamp: number;
  lastActiveTimestamp: number;
  converted?: boolean;
  convertedUserId?: string;
  convertedEmail?: string;
  convertedAt?: number;
}

const generateSessionId = (): string => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 8);
  return `guest_sess_${ts}_${rand}`;
};

const getInitialState = (): GuestTrialState => {
  let sessId = '';
  try {
    sessId = localStorage.getItem(GUEST_SESSION_ID_KEY) || '';
    if (!sessId) {
      sessId = generateSessionId();
      localStorage.setItem(GUEST_SESSION_ID_KEY, sessId);
    }
  } catch {
    sessId = generateSessionId();
  }

  return {
    sessionId: sessId,
    questionsAnswered: 0,
    flashcardsViewed: 0,
    totalItemsExplored: 0,
    examsCompleted: 0,
    answeredQuestionIds: [],
    viewedFlashcardIds: [],
    completedExamIds: [],
    firstActiveTimestamp: Date.now(),
    lastActiveTimestamp: Date.now()
  };
};

export const GuestTrialService = {
  getSessionId(): string {
    try {
      let sessId = localStorage.getItem(GUEST_SESSION_ID_KEY);
      if (!sessId) {
        sessId = generateSessionId();
        localStorage.setItem(GUEST_SESSION_ID_KEY, sessId);
      }
      return sessId;
    } catch {
      return generateSessionId();
    }
  },

  getState(): GuestTrialState {
    try {
      const stored = localStorage.getItem(GUEST_TRIAL_STORAGE_KEY);
      const sessId = this.getSessionId();
      if (!stored) return { ...getInitialState(), sessionId: sessId };
      const parsed = JSON.parse(stored);
      const completedExams = Array.isArray(parsed.completedExamIds) ? parsed.completedExamIds : [];
      return {
        sessionId: parsed.sessionId || sessId,
        questionsAnswered: parsed.questionsAnswered || 0,
        flashcardsViewed: parsed.flashcardsViewed || 0,
        totalItemsExplored: (parsed.questionsAnswered || 0) + (parsed.flashcardsViewed || 0),
        examsCompleted: typeof parsed.examsCompleted === 'number' ? parsed.examsCompleted : completedExams.length,
        answeredQuestionIds: Array.isArray(parsed.answeredQuestionIds) ? parsed.answeredQuestionIds : [],
        viewedFlashcardIds: Array.isArray(parsed.viewedFlashcardIds) ? parsed.viewedFlashcardIds : [],
        completedExamIds: completedExams,
        firstActiveTimestamp: parsed.firstActiveTimestamp || Date.now(),
        lastActiveTimestamp: parsed.lastActiveTimestamp || Date.now(),
        converted: !!parsed.converted,
        convertedUserId: parsed.convertedUserId,
        convertedEmail: parsed.convertedEmail,
        convertedAt: parsed.convertedAt
      };
    } catch {
      return getInitialState();
    }
  },

  saveState(state: GuestTrialState): void {
    try {
      state.sessionId = state.sessionId || this.getSessionId();
      state.totalItemsExplored = state.questionsAnswered + state.flashcardsViewed;
      state.lastActiveTimestamp = Date.now();
      localStorage.setItem(GUEST_TRIAL_STORAGE_KEY, JSON.stringify(state));
      window.dispatchEvent(new CustomEvent('guest-trial-updated', { detail: state }));
    } catch (e) {
      console.warn('Failed to persist guest trial state:', e);
    }
  },

  markConverted(userId: string, email: string): void {
    const state = this.getState();
    state.converted = true;
    state.convertedUserId = userId;
    state.convertedEmail = email;
    state.convertedAt = Date.now();
    this.saveState(state);
  },

  trackQuestionAnswered(questionId: string, limit: number = DEFAULT_GUEST_LIMIT): { allowed: boolean; remaining: number; totalUsed: number } {
    const state = this.getState();
    if (!state.answeredQuestionIds.includes(questionId)) {
      state.answeredQuestionIds.push(questionId);
      state.questionsAnswered = state.answeredQuestionIds.length;
      this.saveState(state);
    }

    const totalUsed = state.totalItemsExplored;
    const remaining = Math.max(0, limit - totalUsed);
    return {
      allowed: totalUsed <= limit,
      remaining,
      totalUsed
    };
  },

  trackFlashcardViewed(cardId: string, limit: number = DEFAULT_GUEST_LIMIT): { allowed: boolean; remaining: number; totalUsed: number } {
    const state = this.getState();
    if (!state.viewedFlashcardIds.includes(cardId)) {
      state.viewedFlashcardIds.push(cardId);
      state.flashcardsViewed = state.viewedFlashcardIds.length;
      this.saveState(state);
    }

    const totalUsed = state.totalItemsExplored;
    const remaining = Math.max(0, limit - totalUsed);
    return {
      allowed: totalUsed <= limit,
      remaining,
      totalUsed
    };
  },

  // Alias for trackFlashcardViewed
  trackFlashcardFlipped(cardId: string, limit: number = DEFAULT_GUEST_LIMIT): { allowed: boolean; remaining: number; totalUsed: number } {
    return this.trackFlashcardViewed(cardId, limit);
  },

  trackExamCompleted(examId: string, limit: number = 1): { allowed: boolean; remaining: number; totalUsed: number } {
    const state = this.getState();
    if (!state.completedExamIds) state.completedExamIds = [];
    state.completedExamIds.push(examId);
    state.examsCompleted = state.completedExamIds.length;
    this.saveState(state);

    const totalUsed = state.examsCompleted;
    const remaining = Math.max(0, limit - totalUsed);
    return {
      allowed: totalUsed <= limit,
      remaining,
      totalUsed
    };
  },

  getExamsCompletedCount(): number {
    const state = this.getState();
    return state.examsCompleted || (state.completedExamIds ? state.completedExamIds.length : 0);
  },

  hasReachedExamLimit(limit: number = 1): boolean {
    const count = this.getExamsCompletedCount();
    return count >= limit;
  },

  getRemainingExamQuota(limit: number = 1): number {
    const count = this.getExamsCompletedCount();
    return Math.max(0, limit - count);
  },

  hasReachedLimit(limit: number = DEFAULT_GUEST_LIMIT): boolean {
    const state = this.getState();
    return state.totalItemsExplored >= limit;
  },

  getRemainingQuota(limit: number = DEFAULT_GUEST_LIMIT): number {
    const state = this.getState();
    return Math.max(0, limit - state.totalItemsExplored);
  },

  reset(): void {
    try {
      localStorage.removeItem(GUEST_TRIAL_STORAGE_KEY);
      window.dispatchEvent(new CustomEvent('guest-trial-updated', { detail: getInitialState() }));
    } catch (e) {
      console.warn('Failed to reset guest trial:', e);
    }
  }
};
